// G10 — Modal de cota estourada do Assistente (DT-12: aberto pelo stream e pelo
// interceptor via `assistant-quota:error`). Self-contained: mostra o bloqueio e,
// num clique, o formulário "pedir mais" (POST `/assistant/quota/requests/`).
//
// Separado do `QuotaBlockedModal` do Hub porque os campos de cota e o endpoint de
// pedido são diferentes (tabela própria D7). Mesma linguagem visual.

import { FC, useState } from 'react';
import assistantQuotaService from '@/services/assistantQuota.service';
import { useToast } from '@/contexts/ToastContext';
import { AssistantQuotaErrorResponse, GeneralQuotaIncreaseField, MyGeneralQuota } from '@/types/assistant';

interface Props {
  error: AssistantQuotaErrorResponse;
  quota: MyGeneralQuota | null;
  onClose: () => void;
}

const MIN_REASON_LEN = 20;

const FIELD_OPTIONS: Array<{ value: GeneralQuotaIncreaseField; label: string }> = [
  { value: 'max_tokens_per_day', label: 'Uso por dia' },
  { value: 'max_tokens_per_month', label: 'Uso por mês' },
  { value: 'max_files_per_day', label: 'Arquivos gerados por dia' },
  { value: 'max_uploads_per_day', label: 'Uploads por dia' },
  { value: 'max_web_searches_per_day', label: 'Buscas na web por dia' },
];

const SCOPE_TO_FIELD: Record<string, GeneralQuotaIncreaseField> = {
  tokens_daily: 'max_tokens_per_day',
  tokens_monthly: 'max_tokens_per_month',
  files_daily: 'max_files_per_day',
  uploads_daily: 'max_uploads_per_day',
  web_searches_daily: 'max_web_searches_per_day',
};

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v ?? 0);
}

const AssistantQuotaModal: FC<Props> = ({ error, quota, onClose }) => {
  const { showToast } = useToast();
  const canRequest = error.scope !== 'model_not_allowed';
  const initialField = SCOPE_TO_FIELD[error.scope] ?? 'max_tokens_per_day';

  const [showRequest, setShowRequest] = useState(false);
  const [field, setField] = useState<GeneralQuotaIncreaseField>(initialField);
  const [suggested, setSuggested] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const used = num(error.details?.used);
  const limit = num(error.details?.limit);
  const reasonOk = reason.trim().length >= MIN_REASON_LEN;
  const currentValue = quota ? (quota[field] as number) : undefined;

  async function submitRequest() {
    if (!reasonOk) {
      showToast(`Explique melhor o motivo (mínimo ${MIN_REASON_LEN} caracteres).`, 'error');
      return;
    }
    setSubmitting(true);
    try {
      await assistantQuotaService.requestIncrease({
        requested_field: field,
        suggested_value: suggested === '' ? undefined : Number(suggested),
        reason: reason.trim(),
      });
      showToast('Pedido enviado! O admin será notificado.', 'success');
      onClose();
    } catch {
      showToast('Não foi possível enviar o pedido.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[55] p-4"
      onClick={onClose}
      data-testid="assistant-quota-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary flex items-center gap-2">
          <span aria-hidden>⏸</span>
          {canRequest ? 'Sua cota acabou' : 'Modelo indisponível'}
        </h2>
        <p className="text-sm text-content-primary">{error.message}</p>
        {canRequest && limit > 0 && (
          <p className="text-sm text-content-tertiary">
            Você usou <strong>{used.toLocaleString('pt-BR')}</strong> de{' '}
            <strong>{limit.toLocaleString('pt-BR')}</strong>.
          </p>
        )}
        <p className="text-xs text-content-tertiary">
          Você continua conseguindo abrir e ler conversas antigas.
        </p>

        {!showRequest && (
          <div className="flex gap-2 pt-3 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              data-testid="aqm-close"
              className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
            >
              Fechar
            </button>
            {canRequest && (
              <button
                type="button"
                onClick={() => setShowRequest(true)}
                data-testid="aqm-request"
                className="flex-1 rounded-lg bg-accent text-surface-primary font-semibold px-4 py-2 text-sm"
              >
                📨 Pedir mais ao admin
              </button>
            )}
          </div>
        )}

        {showRequest && (
          <div className="space-y-3 pt-3 border-t border-surface-border" data-testid="aqm-request-form">
            <label className="block">
              <span className="text-sm font-semibold text-content-primary block mb-1">
                Qual limite aumentar?
              </span>
              <select
                value={field}
                onChange={(e) => setField(e.target.value as GeneralQuotaIncreaseField)}
                data-testid="aqm-field"
                className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
              >
                {FIELD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            {currentValue !== undefined && (
              <div className="text-sm bg-surface-secondary border border-surface-border rounded-lg p-2">
                <span className="text-content-tertiary">Valor atual: </span>
                <strong className="text-content-primary">
                  {currentValue.toLocaleString('pt-BR')}
                </strong>
              </div>
            )}

            <label className="block">
              <span className="text-sm font-semibold text-content-primary block mb-1">
                Sugira novo valor (opcional)
              </span>
              <input
                type="number"
                min={1}
                value={suggested}
                onChange={(e) => setSuggested(e.target.value)}
                data-testid="aqm-suggested"
                placeholder="Deixe vazio se o admin decidir"
                className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
              />
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-content-primary block mb-1">
                Por que você precisa de mais?{' '}
                <span className="text-content-tertiary">(mín {MIN_REASON_LEN} caracteres)</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="aqm-reason"
                rows={3}
                placeholder="Explique como você está usando o H.Bot e por que precisa de mais cota…"
                className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
              />
              <p className={`text-xs mt-1 ${reasonOk ? 'text-green-400' : 'text-content-tertiary'}`}>
                {reason.trim().length}/{MIN_REASON_LEN}+
              </p>
            </label>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitRequest}
                disabled={submitting || !reasonOk}
                data-testid="aqm-submit"
                className="rounded-lg bg-accent text-surface-primary font-semibold px-4 py-2 text-sm disabled:opacity-50"
              >
                {submitting ? 'Enviando…' : 'Enviar pedido'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantQuotaModal;
